import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import VerifiedIcon from '@mui/icons-material/Verified';

const rptRows = [
  { name: 'Haemoglobin', val: '13.8', range: '11.5–16.5 g/dL', fill: 68, tone: 'ok', tag: 'Normal' },
  { name: 'HbA1c', val: '6.1', range: '4.0–5.6 %', fill: 84, tone: 'warn', tag: 'High' },
  { name: 'Vitamin D', val: '11.4', range: '30–100 ng/mL', fill: 22, tone: 'err', tag: 'Low ↓' },
  { name: 'Cholesterol', val: '178', range: '< 200 mg/dL', fill: 55, tone: 'ok', tag: 'Normal' },
  { name: 'TSH', val: '2.1', range: '0.4–4.0 mIU/L', fill: 42, tone: 'ok', tag: 'Normal' },
];

const toneColors = {
  ok: { bar: 'var(--sage)', val: 'var(--sage-l)', tagBg: 'rgba(61,122,82,0.15)', tagFg: 'var(--sage-l)' },
  warn: { bar: '#F59E0B', val: '#FCD34D', tagBg: 'rgba(245,158,11,0.12)', tagFg: '#FCD34D' },
  err: { bar: 'var(--red)', val: '#F87171', tagBg: 'rgba(220,38,38,0.12)', tagFg: '#F87171' },
};

const HeroSection = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'var(--forest)',
        color: '#fff',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        pt: { xs: 8, md: 10 },
        pb: { xs: 5, md: 7 },
      }}
    >
      <Container maxWidth="xl" sx={{ maxWidth: 1300, position: 'relative', zIndex: 2 }}>
        <Grid container columns={12} spacing={{ xs: 3, md: 3, lg: 4 }} alignItems="center">
          <Grid item xs={12} md={7} lg={7}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                bgcolor: 'rgba(200,237,216,0.1)',
                border: '1px solid rgba(200,237,216,0.2)',
                borderRadius: 100,
                pl: 1.75,
                pr: 2.25,
                py: 1,
                mb: 3,
                maxWidth: '100%',
                overflow: 'hidden',
                lineHeight: 1,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: 8,
                  height: 8,
                  flexShrink: 0,
                  alignSelf: 'center',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'var(--sage-l)',
                    borderRadius: '50%',
                    zIndex: 2,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    bgcolor: 'var(--sage-l)',
                    opacity: 0.5,
                    animation: 'pulse 2s ease-in-out infinite',
                  }}
                />
              </Box>
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: 9, sm: 10, md: 12 },
                  fontWeight: 600,
                  color: 'rgba(200,237,216,0.85)',
                  letterSpacing: '0.06em',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                NABL Accredited · ISO 15189:2022 · 4.5★ Users · 1M+ Patients
              </Typography>
            </Box>

            <Typography
              variant="h1"
              className="serif"
              sx={{
                color: '#fff',
                fontSize: { xs: 24, sm: 32, md: 48, lg: 64 },
                lineHeight: 1.06,
                mb: { xs: 1.5, md: 3 },
                '& em': { fontStyle: 'italic', color: 'var(--sage-l)' },
              }}
            >
              Precision health<br />
              diagnostics,<br />
              <em>delivered to you.</em>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 12, sm: 13, md: 15, lg: 17 },
                lineHeight: 1.7,
                color: 'rgba(255,255,255,0.5)',
                maxWidth: 460,
                fontWeight: 300,
              }}
            >
              Book any of 1700+ tests online. Our certified Trustian arrives at your home within 60 minutes.
              Doctor-verified results in 6 hours — sent directly to WhatsApp.
            </Typography>
          </Grid>

          <Grid item xs={12} md={5} lg={5}>
            <Stack spacing={2}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: { xs: '12px', md: '20px' },
                  p: { xs: 1.25, sm: 1.75, md: 2.75 },
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{
                    mb: { xs: 1.25, md: 2.5 },
                    pb: { xs: 1, md: 2 },
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      className="mono"
                      sx={{
                        fontSize: { xs: 7, sm: 8, md: 10 },
                        color: 'rgba(200,237,216,0.5)',
                        letterSpacing: '0.14em',
                        mb: 0.5,
                      }}
                    >
                      DIAGNOSTIC REPORT
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 10, sm: 11, md: 15 }, fontWeight: 700, color: '#fff' }}>
                      Full Body Checkup{' '}
                      <Typography
                        component="span"
                        sx={{ fontSize: { xs: 8, sm: 9, md: 12 }, fontWeight: 400, color: 'rgba(255,255,255,0.35)' }}
                      >
                        · 91 Parameters
                      </Typography>
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: 'rgba(106,174,128,0.12)',
                        border: '1px solid rgba(106,174,128,0.25)',
                        borderRadius: 100,
                        px: { xs: 0.5, md: 1.25 },
                        py: { xs: 0.25, md: 0.5 },
                        lineHeight: 1,
                      }}
                    >
                      <VerifiedIcon
                        sx={{
                          fontSize: { xs: 8, md: 11 },
                          color: '#6AAE80',
                          display: 'block',
                        }}
                      />
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: 7, sm: 8, md: 11 },
                          fontWeight: 700,
                          color: 'var(--sage-l)',
                          lineHeight: 1,
                        }}
                      >
                        Doctor Verified
                      </Typography>
                    </Box>
                    <Typography
                      className="mono"
                      sx={{ fontSize: { xs: 7, sm: 8, md: 10 }, color: 'rgba(255,255,255,0.25)', mt: 0.5 }}
                    >
                      28 Mar 2026 · 1:42 PM
                    </Typography>
                  </Box>
                </Stack>

                {rptRows.map((row) => {
                  const t = toneColors[row.tone];
                  return (
                    <Box
                      key={row.name}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '54px 1fr 28px 38px',
                          sm: '64px 1fr 30px 42px',
                          md: '90px 1fr 38px 52px',
                        },
                        gap: { xs: 0.5, sm: 0.75, md: 1.25 },
                        alignItems: 'center',
                        py: { xs: 0.5, sm: 0.75, md: 1.25 },
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: 8, sm: 9, md: 12 },
                          fontWeight: 600,
                          color: 'rgba(255,255,255,0.55)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.name}
                      </Typography>
                      <Box>
                        <Box sx={{ height: { xs: 3, md: 5 }, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 99, position: 'relative' }}>
                          <Box
                            sx={{
                              height: { xs: 3, md: 5 },
                              borderRadius: 99,
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              width: `${row.fill}%`,
                              bgcolor: t.bar,
                            }}
                          />
                          <Box
                            sx={{
                              width: { xs: 6, md: 9 },
                              height: { xs: 6, md: 9 },
                              borderRadius: '50%',
                              bgcolor: t.bar,
                              position: 'absolute',
                              top: { xs: -1.5, md: -2 },
                              left: `${row.fill}%`,
                              marginLeft: { xs: '-3px', md: '-4px' },
                              boxShadow: `0 0 6px ${t.bar}`,
                            }}
                          />
                        </Box>
                        <Typography
                          className="mono"
                          sx={{
                            fontSize: { xs: 6, sm: 7, md: 9 },
                            color: 'rgba(255,255,255,0.2)',
                            letterSpacing: '0.04em',
                            mt: 0.5,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.range}
                        </Typography>
                      </Box>
                      <Typography
                        className="mono"
                        sx={{ fontSize: { xs: 9, sm: 10, md: 14 }, fontWeight: 500, textAlign: 'right', color: t.val }}
                      >
                        {row.val}
                      </Typography>
                      <Box
                        sx={{
                          fontSize: { xs: 7, sm: 8, md: 10 },
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textAlign: 'center',
                          borderRadius: 100,
                          py: { xs: 0.25, md: 0.375 },
                          px: { xs: 0.5, md: 1 },
                          bgcolor: t.tagBg,
                          color: t.tagFg,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.tag}
                      </Box>
                    </Box>
                  );
                })}

                {/* Summary footer */}
                <Box
                  sx={{
                    mt: { xs: 1, md: 1.75 },
                    pt: { xs: 1, md: 1.75 },
                    borderTop: '1px solid rgba(255,255,255,0.07)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: { xs: 'center', md: 'space-between' },
                    rowGap: { xs: 0.75, md: 1 },
                    columnGap: { xs: 1, md: 1.5 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'center',
                      rowGap: 0.5,
                      columnGap: { xs: 1, sm: 1.25, md: 1.5 },
                    }}
                  >
                    {[
                      { c: 'var(--sage-l)', label: '3 Normal' },
                      { c: '#FCD34D', label: '1 High' },
                      { c: '#F87171', label: '1 Low' },
                    ].map((s) => (
                      <Stack key={s.label} direction="row" spacing={0.5} alignItems="center">
                        <Box
                          sx={{
                            width: { xs: 5, md: 7 },
                            height: { xs: 5, md: 7 },
                            borderRadius: '50%',
                            bgcolor: s.c,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: 8, sm: 9, md: 11 },
                            color: 'rgba(255,255,255,0.6)',
                            fontWeight: 500,
                            lineHeight: 1,
                          }}
                        >
                          {s.label}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.625,
                      lineHeight: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 14, sm: 16, md: 22 },
                        height: { xs: 14, sm: 16, md: 22 },
                        borderRadius: '50%',
                        bgcolor: '#25D366',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        lineHeight: 0,
                      }}
                    >
                      <WhatsAppIcon
                        sx={{
                          fontSize: { xs: 9, sm: 10, md: 14 },
                          color: '#fff',
                          display: 'block',
                        }}
                      />
                    </Box>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: 8, sm: 9, md: 11 },
                        color: 'rgba(255,255,255,0.6)',
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      Sent to WhatsApp · 1:47 PM
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: { xs: '10px', md: '16px' },
                  p: { xs: 1.25, sm: 1.5, md: 2.25 },
                  display: 'flex',
                  gap: { xs: 1, md: 1.75 },
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 24, sm: 28, md: 38 },
                    height: { xs: 24, sm: 28, md: 38 },
                    borderRadius: '50%',
                    bgcolor: 'rgba(61,122,82,0.25)',
                    border: '1.5px solid rgba(61,122,82,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: 11, sm: 13, md: 17 },
                    flexShrink: 0,
                  }}
                >
                  👨‍⚕️
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: 9, sm: 10, md: 12 },
                      fontWeight: 700,
                      color: 'rgba(255,255,255,0.9)',
                      mb: 0.5,
                    }}
                  >
                    Dr. Akhil · MD
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 9, sm: 10, md: 12 },
                      color: 'rgba(255,255,255,0.38)',
                      lineHeight: 1.6,
                      fontWeight: 300,
                    }}
                  >
                    Vitamin D is critically low — recommend D3 2000 IU daily for 3 months. HbA1c at pre-diabetic range;
                    diet review advised.
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
